import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import i18n from '@/i18n/config';

/**
 * Maps i18n locale to default country code for phone input
 */
export function getDefaultCountryFromLocale(): CountryCode {
  const lang = i18n.language;

  switch (lang) {
    case 'pt-BR':
      return 'BR';
    case 'pt-PT':
      return 'PT';
    case 'de':
      return 'DE';
    case 'es':
      return 'ES';
    case 'fr':
      return 'FR';
    case 'it':
      return 'IT';
    case 'pl':
      return 'PL';
    default:
      return 'US';
  }
}

/**
 * Validates a phone number in E.164 format (output from PhoneInput)
 * Returns true if valid, false if invalid
 * Empty/undefined values return true (for optional fields)
 */
export function validatePhoneNumber(phone: string | undefined | null): boolean {
  if (!phone || phone === '') {
    return true; // Empty is valid for optional fields
  }

  try {
    return isValidPhoneNumber(phone);
  } catch {
    return false;
  }
}

/**
 * Gets the country code from an E.164 phone number
 */
export function getCountryFromPhone(phone: string): CountryCode | undefined {
  if (!phone) return undefined;

  try {
    const parsed = parsePhoneNumber(phone);
    return parsed?.country;
  } catch {
    return undefined;
  }
}

/**
 * Zod refinement function for phone validation
 * Use with .refine() in Zod schemas
 */
export const phoneValidationRefinement = (phone: string | undefined | null): boolean => {
  return validatePhoneNumber(phone);
};

/**
 * Default error message for invalid phone numbers
 */
export const phoneValidationMessage = 'Please enter a valid phone number for the selected country';
